import type { ExtendedMedia } from '@seen/core/models/Media'
import { IconPhoto } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { FullScreenGallery } from '#src/components/memories/FullScreenGallery.tsx'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

interface MemoriesGridProps {
  media: ExtendedMedia[]
}

export function MemoriesGrid({ media }: MemoriesGridProps): JSX.Element {
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)

  const openGallery = (index: number): void => {
    setGalleryIndex(index)
    setIsGalleryOpen(true)
  }

  const navigateGallery = (index: number): void => {
    setGalleryIndex(index)
  }

  if (media.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <IconPhoto className="h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-medium text-foreground">
          No memories found
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          No images have been found for this participant yet.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {media.map((mediaItem, index) => (
          <MemoryCard
            key={mediaItem.id}
            media={mediaItem}
            onClick={() => openGallery(index)}
          />
        ))}
      </div>

      {galleryIndex !== null && (
        <FullScreenGallery
          media={media}
          currentIndex={galleryIndex}
          open={isGalleryOpen}
          onOpenChange={setIsGalleryOpen}
          onNavigate={navigateGallery}
        />
      )}
    </>
  )
}

interface MemoryCardProps {
  media: ExtendedMedia
  onClick: () => void
}

function MemoryCard({ media, onClick }: MemoryCardProps): JSX.Element {
  const stableUrl = useStableAttachmentUrl(media.contentUrl)
  const patientAssociation = media.patientAssociation

  return (
    <Card
      className="group cursor-pointer overflow-hidden p-0 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
      onClick={onClick}
    >
      {stableUrl && media.contentType?.startsWith('image/') ? (
        <div className="relative">
          <img
            src={stableUrl}
            alt={media.title ?? 'Memory image'}
            className="h-48 w-full object-cover"
          />
          {patientAssociation && (
            <div className="absolute right-0 bottom-0 left-0 bg-black/70 p-2 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="text-xs">
                <div className="flex justify-between">
                  <span>Confidence:</span>
                  <span className="font-medium">
                    {patientAssociation.similarity.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-48 items-center justify-center bg-muted">
          <IconPhoto className="h-12 w-12 text-muted-foreground" />
        </div>
      )}
      <div className="p-3">
        <div className="mt-1 text-xs text-muted-foreground">
          {media.createdDateTime && (
            <div className="truncate">
              {new Date(media.createdDateTime).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
